import React from 'react';
import { Alert as MantineAlert, Text, Title } from '@mantine/core';
import { InfoCircledIcon, ExclamationTriangleIcon, CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons'; // Common icons for alerts

interface AlertProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'filled' | 'light' | 'outline' | 'transparent' | 'white'; // Mantine alert variants
  color?: string; // Mantine color prop
  title?: React.ReactNode; // Explicitly add title to props
  icon?: React.ReactNode;  // Explicitly add icon to props
  // Add other props if App.tsx passes them, e.g., withCloseButton
}

// A simple mapping if your App.tsx implies severity via classNames or props not directly mapped
const getAlertIconAndColor = (className?: string) => {
  if (className?.includes('success') || className?.includes('green')) return { icon: <CheckCircledIcon />, color: 'green' };
  if (className?.includes('warning') || className?.includes('amber') || className?.includes('yellow')) return { icon: <ExclamationTriangleIcon />, color: 'yellow' };
  if (className?.includes('error') || className?.includes('red')) return { icon: <CrossCircledIcon />, color: 'red' };
  return { icon: <InfoCircledIcon />, color: undefined }; // Default or info
};

export const Alert = ({ children, className, variant='light', color, title, icon, ...otherProps }: AlertProps) => {
  const { icon: defaultIcon, color: defaultColor } = getAlertIconAndColor(className);
  return (
    <MantineAlert 
      variant={variant} 
      color={color || defaultColor} 
      title={title} // Use the destructured title prop
      icon={icon || defaultIcon} // Use the destructured icon prop
      className={className} 
      {...otherProps}
    >
      {children}
    </MantineAlert>
  );
}

interface AlertTitleProps {
  children: React.ReactNode;
  className?: string;
}

// Mantine Alert has a title prop, so this component might become redundant
// or used to wrap the title content if passed as children to Alert
export const AlertTitle = ({ children, className }: AlertTitleProps) => (
  <Title order={5} className={className}>{children}</Title> 
);

interface AlertDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

// Children of MantineAlert are typically the description
export const AlertDescription = ({ children, className }: AlertDescriptionProps) => (
  <Text size="sm" className={className}>{children}</Text>
); 